import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Heart, MessageCircle, Share2, MoreHorizontal, User, Trash2, Flag, Eye, Sparkles, Zap } from 'lucide-react';
import { doc, setDoc, deleteDoc, updateDoc, increment, addDoc, collection, getDoc, writeBatch, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { getAnonymousName, getAvatarForShadow } from '../../utils/shadowIdentity';
import { ShadowPollView } from './ShadowPollView';
import { usePopup } from '../../context/PopupContext';
import { toPng } from 'html-to-image';

const formatRelativeTime = (timestamp) => {
  if (!timestamp) return 'Just now';
  let date;
  if (timestamp.toDate) date = timestamp.toDate();
  else if (timestamp.seconds) date = new Date(timestamp.seconds * 1000);
  else date = new Date(timestamp);
  
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 60) return 'Just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
};

export const ShadowCard = ({ 
  shadow, 
  isLiked, 
  onLikeUpdate,
  onDelete,
  onReport
}) => {
  const navigate = useNavigate();
  const { showConfirm } = usePopup();
  const currentUser = auth.currentUser;
  
  const [showOptions, setShowOptions] = useState(false);
  const [isLiking, setIsLiking] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const cardRef = React.useRef(null);
  const snapshotRef = React.useRef(null);
  const hasSharedRef = React.useRef(false);

  const [reportModalVisible, setReportModalVisible] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [submittingReport, setSubmittingReport] = useState(false);
  const [successModalVisible, setSuccessModalVisible] = useState(false);

  const initialLikeCount = Array.isArray(shadow.likes) ? shadow.likes.length : (typeof shadow.likes === 'number' ? shadow.likes : 0);
  const [localLikeCount, setLocalLikeCount] = useState(initialLikeCount);
  const [localIsLiked, setLocalIsLiked] = useState(isLiked);

  const initialShareCount = shadow.shareCount || shadow.shares || 0;
  const [localShareCount, setLocalShareCount] = useState(initialShareCount);
  const [showHeartBurst, setShowHeartBurst] = useState(false);

  useEffect(() => {
    setLocalIsLiked(isLiked);
  }, [isLiked]);

  // Check DB Persistence for previous shares by this user (matching ConfessionCard.js)
  useEffect(() => {
    if (!currentUser?.uid || !shadow?.id) return;
    let isSubscribed = true;
    const checkUserShare = async () => {
      try {
        const userShareRef = doc(db, 'users', currentUser.uid, 'confShares', shadow.id);
        const shareDoc = await getDoc(userShareRef);
        if (shareDoc.exists() && isSubscribed) {
          hasSharedRef.current = true;
        }
      } catch (e) {}
    };
    checkUserShare();
    return () => { isSubscribed = false; };
  }, [currentUser?.uid, shadow?.id]);
  
  // Use Shadow Identity logic based on the poster's UID (or the logged-in user if their own post)
  // For shadows, the actual userProfile is hidden.
  const uidToHash = shadow.userId || shadow.uid;
  const shadowName = getAnonymousName(uidToHash);
  const shadowAvatar = getAvatarForShadow(shadowName);

  const isOwnPost = currentUser?.uid === uidToHash;

  const handleLike = async (e) => {
    e.stopPropagation();
    if (!currentUser || isLiking) return;
    
    setIsLiking(true);
    const previousIsLiked = localIsLiked;
    const previousLikeCount = localLikeCount;
    setLocalIsLiked(!previousIsLiked);
    setLocalLikeCount(prev => previousIsLiked ? Math.max(0, prev - 1) : prev + 1);

    if (!previousIsLiked) {
        setShowHeartBurst(true);
        setTimeout(() => setShowHeartBurst(false), 800);
    }

    const likeRef = doc(db, 'users', currentUser.uid, 'likedConf', shadow.id);
    const postRef = doc(db, 'confessions', shadow.id);
    
    try {
      if (previousIsLiked) {
        onLikeUpdate(shadow.id, false);
        await deleteDoc(likeRef);
        await updateDoc(postRef, { likes: increment(-1) });
      } else {
        onLikeUpdate(shadow.id, true);
        await setDoc(likeRef, { likedAt: new Date(), isActive: true }, { merge: true });
        await updateDoc(postRef, { likes: increment(1) });
        
        if (currentUser.uid !== uidToHash) {
          try {
            await addDoc(collection(db, 'users', uidToHash, 'notifications'), {
              type: 'like',
              fromUserId: currentUser.uid,
              toUserId: uidToHash,
              confessionId: shadow.id,
              createdAt: new Date(),
              read: false
            });
          } catch (e) {
            console.error("Failed to send like notification", e);
          }
        }
      }
    } catch (err) {
      console.error("Like failed", err);
      setLocalIsLiked(previousIsLiked);
      setLocalLikeCount(previousLikeCount);
      onLikeUpdate(shadow.id, previousIsLiked);
    } finally {
      setIsLiking(false);
    }
  };

  const handleShare = async (e) => {
    e.stopPropagation();
    if (isSharing) return;
    setIsSharing(true);

    const url = `${window.location.origin}/shadow/${shadow.id}`;

    // Check DB Persistence & Local Ref to prevent duplicate share increments (matching ConfessionCard.js)
    if (!hasSharedRef.current && currentUser?.uid) {
      try {
        const userShareRef = doc(db, 'users', currentUser.uid, 'confShares', shadow.id);
        const shareDoc = await getDoc(userShareRef);

        if (shareDoc.exists()) {
          hasSharedRef.current = true;
        } else {
          const batch = writeBatch(db);
          const postRef = doc(db, 'confessions', shadow.id);

          batch.set(userShareRef, { sharedAt: serverTimestamp() }, { merge: true });
          batch.update(postRef, {
            shareCount: increment(1),
            shares: increment(1)
          });

          await batch.commit();
          hasSharedRef.current = true;
          setLocalShareCount(prev => prev + 1);
        }
      } catch (err) {
        console.error("Failed to update share count", err);
      }
    }

    try {
      if (snapshotRef.current) {
        toast.info("Generating snapshot...", { id: 'snapshot' });

        // Pre-fetch any image via server-side proxy to bypass CORS for canvas
        if (shadow.imageUrl) {
          try {
            const imgEl = snapshotRef.current.querySelector('[data-snapshot-image]');
            if (imgEl) {
              const proxyUrl = `/api/proxy-image?url=${encodeURIComponent(shadow.imageUrl)}`;
              const response = await fetch(proxyUrl);
              const blob = await response.blob();
              const reader = new FileReader();
              const dataUrlPromise = new Promise((resolve) => {
                reader.onloadend = () => resolve(reader.result);
                reader.readAsDataURL(blob);
              });
              const base64 = await dataUrlPromise;
              // Set src and wait for the image to fully load and decode
              await new Promise((resolve, reject) => {
                imgEl.onload = () => {
                  // Use decode() if available for guaranteed paint
                  if (imgEl.decode) {
                    imgEl.decode().then(resolve).catch(resolve);
                  } else {
                    resolve();
                  }
                };
                imgEl.onerror = () => resolve(); // Don't block on error
                imgEl.src = base64;
              });
            }
          } catch (imgErr) {
            console.warn('Could not pre-fetch image for snapshot', imgErr);
          }
        }

        const dataUrl = await toPng(snapshotRef.current, { cacheBust: true, backgroundColor: '#0a0a0c' });
        
        try {
            // Attempt to share the image as a file
            const blob = await (await fetch(dataUrl)).blob();
            const file = new File([blob], `spark-shadow-${shadow.id}.png`, { type: 'image/png' });
            
            if (navigator.canShare && navigator.canShare({ files: [file] })) {
                await navigator.share({
                    text: `Check out this Shadow on Spark!\n${url}`,
                    files: [file]
                });
                toast.dismiss('snapshot');
                return;
            }
        } catch(shareErr) {
            console.error("Native share failed", shareErr);
        }

        // Fallback: download the image and copy the URL
        const link = document.createElement('a');
        link.download = `spark-shadow-${shadow.id}.png`;
        link.href = dataUrl;
        link.click();
        
        navigator.clipboard.writeText(url);
        toast.dismiss('snapshot');
        toast.success('Snapshot saved & link copied to clipboard!');
      }
    } catch (error) {
        console.error("Failed to generate snapshot", error);
        toast.dismiss('snapshot');
        
        // Final fallback: just share URL
        if (navigator.share) {
            navigator.share({
              title: 'Check out this shadow on Spark',
              url: url
            }).catch(console.error);
        } else {
            navigator.clipboard.writeText(url);
            toast.success('Link copied to clipboard!');
        }
    } finally {
        setIsSharing(false);
    }
  };

  const handleDelete = async (e) => {
    e.stopPropagation();
    showConfirm({
      title: "Delete Shadow",
      message: "Are you sure you want to delete this shadow? This action cannot be undone.",
      isDestructive: true,
      confirmText: "Delete",
      onConfirm: async () => {
        try {
          await deleteDoc(doc(db, 'confessions', shadow.id));
          if (onDelete) onDelete(shadow.id);
          toast.success("Shadow deleted");
        } catch (err) {
          console.error("Error deleting shadow", err);
          toast.error("Failed to delete shadow");
        }
      }
    });
  };

  const handleReport = (e) => {
    e.stopPropagation();
    if (onReport) {
      onReport();
    } else {
      setReportModalVisible(true);
    }
    setShowOptions(false);
  };

  const handleReportSubmit = async (e) => {
    e.stopPropagation();
    if (!reportReason.trim() || submittingReport || !currentUser) return;
    setSubmittingReport(true);
    try {
      await addDoc(collection(db, 'confessionReports'), {
        confessionId: shadow.id,
        userId: currentUser.uid,
        reason: reportReason.trim(),
        createdAt: new Date(),
        status: 'pending'
      });
      setReportModalVisible(false);
      setReportReason('');
      setSuccessModalVisible(true);
      setTimeout(() => setSuccessModalVisible(false), 3000);
    } catch (err) {
      console.error(err);
      toast.error('Report failed');
    } finally {
      setSubmittingReport(false);
    }
  };

  const navigateToPost = () => {
    navigate(`/shadow/${shadow.id}`);
  };

  return (
    <div 
      ref={cardRef}
      onClick={navigateToPost}
      className="bg-black p-4 sm:p-5 border-b border-gray-800 hover:bg-[#080808] transition-all cursor-pointer relative"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gray-800 overflow-hidden flex items-center justify-center shrink-0 border border-gray-700">
             {shadowAvatar ? (
               <img src={shadowAvatar} alt={shadowName} className="w-full h-full object-cover" />
             ) : (
               <User className="w-6 h-6 text-gray-500" />
             )}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-bold text-white text-[15px] hover:underline">
                {shadowName}
              </span>
              {shadow.authorIsPremium && (
                <Sparkles size={14} className="text-[#0BD9E5] shrink-0" />
              )}
              {shadow.college && (
                <span className="text-[11px] text-[#0BD9E5] bg-[#0BD9E5]/10 px-2 py-0.5 rounded-full font-medium">
                  {shadow.college}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 text-gray-500 text-[13px]">
              <span>{formatRelativeTime(shadow.createdAt)}</span>
              {shadow.visibility === 'global' && (
                <>
                  <span>•</span>
                  <span>Global</span>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="relative">
          <button 
            onClick={(e) => {
              e.stopPropagation();
              setShowOptions(!showOptions);
            }}
            className="p-2 text-gray-500 hover:text-white rounded-full hover:bg-white/10 transition-colors"
          >
            <MoreHorizontal size={20} />
          </button>
          
          {showOptions && (
            <div className="absolute right-0 top-10 w-48 bg-[#1a1b23] border border-gray-800 rounded-xl shadow-xl overflow-hidden z-20">
              {isOwnPost ? (
                <button 
                  onClick={handleDelete}
                  className="w-full px-4 py-3 text-left hover:bg-white/5 transition-colors flex items-center gap-3 text-red-500 font-medium"
                >
                  <Trash2 size={16} /> Delete Shadow
                </button>
              ) : (
                <button 
                  onClick={handleReport}
                  className="w-full px-4 py-3 text-left hover:bg-white/5 transition-colors flex items-center gap-3 text-white font-medium"
                >
                  <Flag size={16} /> Report Shadow
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="mb-4">
        <p className="text-gray-100 text-[15px] leading-relaxed whitespace-pre-wrap font-medium">
          {shadow.content}
        </p>
        {shadow.imageUrl && (
          <div className="mt-3 rounded-xl overflow-hidden border border-gray-800 bg-gray-900/50">
             <img src={shadow.imageUrl} alt="Shadow attachment" className="w-full object-contain" loading="lazy" />
          </div>
        )}
        {/* GIF support */}
        {(shadow.gifUrl || shadow.gif?.url) && (
          <div className="mt-3 rounded-xl overflow-hidden border border-gray-800 bg-gray-900/50">
             <img src={shadow.gifUrl || shadow.gif.url} alt="Shadow GIF" className="w-full object-contain" loading="lazy" />
          </div>
        )}
      </div>

      {shadow.tags && shadow.tags.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {shadow.tags.map((tag, index) => (
            <span key={index} className="px-3 py-1 bg-gray-800/80 border border-gray-700 text-[#0BD9E5] text-[12px] font-medium rounded-full cursor-pointer hover:bg-gray-800 transition-colors">
              #{tag.replace('#', '')}
            </span>
          ))}
        </div>
      )}

      {/* Media Preview if exists */}
      {shadow.mediaUrl && (
         <div className="rounded-xl overflow-hidden border border-gray-800 mb-4 bg-gray-900">
             <img src={shadow.mediaUrl} alt="Shadow media" className="w-full object-contain" />
         </div>
      )}

      {/* Poll */}
      {shadow.type === 'poll' && (
          <ShadowPollView confession={shadow} />
      )}

      {/* Interactions */}
      <div className="flex items-center justify-between border-t border-gray-800 pt-3 mt-2">
        <button 
          onClick={handleLike}
          className={`flex items-center gap-2 group transition-colors ${localIsLiked ? 'text-pink-500' : 'text-gray-400 hover:text-pink-500'}`}
        >
          <div className={`p-2 rounded-full transition-colors relative ${localIsLiked ? 'bg-pink-500/10' : 'group-hover:bg-pink-500/10'}`}>
            <Heart size={20} className={`transition-transform duration-300 ${localIsLiked ? "fill-current scale-110" : "scale-100"}`} />
            {showHeartBurst && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <Heart size={30} className="text-pink-500 animate-ping absolute opacity-75 fill-current" />
                </div>
            )}
          </div>
          <span className="text-sm font-medium">{localLikeCount > 0 ? localLikeCount : ''}</span>
        </button>

        <button 
          onClick={(e) => { e.stopPropagation(); navigateToPost(); }}
          className="flex items-center gap-2 text-gray-400 hover:text-[#0BD9E5] group transition-colors"
        >
          <div className="p-2 rounded-full group-hover:bg-[#0BD9E5]/10 transition-colors">
            <MessageCircle size={20} />
          </div>
          <span className="text-sm font-medium">{shadow.commentCount > 0 ? shadow.commentCount : ''}</span>
        </button>

        <div className="flex items-center gap-2 text-gray-400">
          <div className="p-2">
            <Eye size={20} />
          </div>
          <span className="text-sm font-medium">{shadow.views > 0 ? shadow.views : ''}</span>
        </div>

        <button 
          onClick={handleShare}
          className="flex items-center gap-2 text-gray-400 hover:text-green-500 group transition-colors"
        >
          <div className="p-2 rounded-full group-hover:bg-green-500/10 transition-colors">
            <Share2 size={20} />
          </div>
          <span className="text-sm font-medium">{localShareCount > 0 ? localShareCount : ''}</span>
        </button>
      </div>

      {/* Report Modal Native */}
      {reportModalVisible && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 px-4" onClick={(e) => e.stopPropagation()}>
          <div className="bg-[#111] border border-gray-800 rounded-2xl w-full max-w-sm p-6 cursor-default">
            <h3 className="text-lg font-bold text-white mb-4 text-center">Report Shadow</h3>
            <textarea
              value={reportReason}
              onChange={(e) => setReportReason(e.target.value)}
              placeholder="Why are you reporting this?"
              className="w-full bg-[#1a1a1a] border border-gray-800 rounded-xl p-4 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 resize-none h-28 mb-2"
              autoFocus
            />
            <div className="flex justify-between gap-3 mt-4">
               <button onClick={(e) => { e.stopPropagation(); setReportModalVisible(false); setReportReason(''); }} className="flex-1 px-4 py-3 bg-[#2a2a2a] text-gray-400 hover:text-white font-medium rounded-xl transition-colors">Cancel</button>
               <button onClick={handleReportSubmit} disabled={reportReason.trim().length < 10 || submittingReport} className="flex-1 px-4 py-3 bg-[#1a1a1a] border border-gray-600 text-white font-bold rounded-xl hover:bg-gray-800 disabled:opacity-50 transition-colors">
                  {submittingReport ? 'Sending...' : 'Submit'}
               </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal Native */}
      {successModalVisible && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 px-4" onClick={(e) => e.stopPropagation()}>
          <div className="bg-[#111] border border-gray-800 rounded-2xl p-8 flex flex-col items-center text-center max-w-sm w-full cursor-default">
            <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mb-4">
              <Flag size={32} className="text-green-500" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">Report Submitted</h3>
            <p className="text-gray-400 text-[15px] mb-6">Thank you for helping keep Spark safe. We'll review this shadow shortly.</p>
            <button onClick={(e) => { e.stopPropagation(); setSuccessModalVisible(false); }} className="w-full py-3 bg-gray-800 hover:bg-gray-700 text-white font-bold rounded-xl transition-colors">
              Close
            </button>
          </div>
        </div>
      )}

      {/* Latest Comment */}
      {shadow.latestComment && shadow.latestComment.content && (
        <div className="mt-3 bg-gray-900/50 rounded-xl p-3 border border-gray-800 flex items-start gap-3">
            <div className="w-7 h-7 rounded-full bg-gray-800 overflow-hidden shrink-0 border border-gray-700">
               {getAvatarForShadow(getAnonymousName(shadow.latestComment.userId)) ? (
                   <img src={getAvatarForShadow(getAnonymousName(shadow.latestComment.userId))} alt="Avatar" className="w-full h-full object-cover" />
               ) : (
                   <User className="w-4 h-4 text-gray-500 m-1.5" />
               )}
            </div>
            <div className="flex-1 min-w-0 flex items-center pt-0.5">
                <span className="text-[13px] font-bold text-gray-300 mr-2 shrink-0">
                    {getAnonymousName(shadow.latestComment.userId)}
                </span>
                <span className="text-[13px] text-gray-400 truncate flex-1 min-w-0 font-medium" title={shadow.latestComment.content}>
                    {shadow.latestComment.content.length > 70 ? shadow.latestComment.content.slice(0, 70) + '...' : shadow.latestComment.content}
                </span>
            </div>
        </div>
      )}

      {/* HIDDEN SNAPSHOT COMPONENT FOR SHARING */}
      <div className="fixed -left-[9999px] top-0 pointer-events-none">
        <div ref={snapshotRef} style={{ backgroundColor: '#0d0d12', backgroundImage: 'linear-gradient(to bottom right, #0d0d12, #000000)', color: '#ffffff' }} className="w-[600px] border border-gray-800 p-8 flex flex-col h-auto">
          
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-gray-800 overflow-hidden flex items-center justify-center shrink-0 border-2 border-gray-700 shadow-xl">
                 {shadowAvatar ? (
                   <img src={shadowAvatar} alt={shadowName} className="w-full h-full object-cover" crossOrigin="anonymous" />
                 ) : (
                   <User className="w-8 h-8 text-gray-500" />
                 )}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-white text-xl">
                    {shadowName}
                  </span>
                  {shadow.college && (
                    <span className="text-sm text-gray-400 uppercase tracking-wider font-bold ml-2">
                      {shadow.college}
                    </span>
                  )}
                </div>
                <div className="text-gray-500 text-[15px] mt-1 font-medium">
                  {formatRelativeTime(shadow.createdAt)}
                </div>
              </div>
            </div>
          </div>

          {/* Main Content Area */}
          <div className="flex-1 flex flex-col justify-center my-4">
             <p className="text-white text-2xl leading-relaxed whitespace-pre-wrap font-medium">
               {shadow.content}
             </p>
             
             {shadow.imageUrl && (
               <div className="mt-4 rounded-xl shadow-xl flex w-full">
                  <img data-snapshot-image="true" src={shadow.imageUrl} alt="Shadow attachment" className="w-full h-auto rounded-xl object-contain" />
               </div>
             )}

             {(shadow.gifUrl || shadow.gif?.url) && (
               <div className="mt-4 rounded-xl shadow-xl flex w-full">
                  <img src={`${shadow.gifUrl || shadow.gif.url}${(shadow.gifUrl || shadow.gif.url).includes('?') ? '&' : '?'}cb=${Date.now()}`} alt="Shadow GIF" className="w-full h-auto rounded-xl object-contain" crossOrigin="anonymous" />
               </div>
             )}
             
             {shadow.type === 'poll' && shadow.pollOptions && (
               <div className="mt-6 space-y-3 w-full">
                 {shadow.pollOptions.map((opt, idx) => (
                   <div key={idx} style={{ backgroundColor: '#1a1b23', borderColor: '#374151' }} className="rounded-xl p-4 border">
                     <span className="text-white text-lg font-bold">{opt.text}</span>
                   </div>
                 ))}
                 <div className="text-gray-500 text-sm font-medium mt-2">
                   {shadow.totalVotes || 0} votes
                 </div>
               </div>
             )}
          </div>

          {/* Footer Branding */}
          <div className="pt-6 border-t border-gray-800 flex justify-center mt-auto">
             <div className="text-gray-400 text-[15px] font-medium italic text-center w-full">
                Explore more on <span className="text-[#0BD9E5]">sparkpl.com</span>
             </div>
          </div>
          
        </div>
      </div>

    </div>
  );
};
